import axios from 'axios';
import _ from 'lodash';
import { AuthManager } from './auth';
import { StoreManager } from './store';
import { ApiManager } from './api';
import { PollingManager } from './polling';
import { QueryContext } from './query';

export class DirectusClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.token = null;
    this.language = null;
    this.headerBasedEnabled = true;
    
    this._queries = new Map();
    this._filterConfigs = new Map();

    this.axios = axios.create({ baseURL: this.baseURL });

    this.auth = new AuthManager(this);
    this.store = new StoreManager();
    this.api = new ApiManager(this);
    this.polling = new PollingManager(this);

    this._setupInterceptors();
  }

  _setupInterceptors() {
    this.axios.interceptors.request.use(
      (config) => {
        if (this.headerBasedEnabled) {
          const customHeaders = this.auth.getCustomHeader();
          config.headers = { ...config.headers, ...customHeaders };
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axios.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );
  }

  setToken(token) { return this.auth.setToken(token); }
  clearToken() { return this.auth.clearToken(); }
  isAuthenticated() { return this.auth.isAuthenticated(); }
  setHeaderBasedEnabled(enabled) { this.headerBasedEnabled = enabled; return this; }
  setStorageProvider(provider) { return this.auth.setStorageProvider(provider); }
  setAuthConfig(config) { return this.auth.setAuthConfig(config); }
  autoAuthSync(storageKey, callback) { 
    this.auth.autoAuthSync(storageKey, callback); 
    return this; 
  }
  setLanguage(lang) { this.language = lang; return this; }

  setFilter(...args) {
    if (args.length < 2) throw new Error('setFilter requires at least 2 arguments');
    
    const optionKey = args[args.length - 1];
    const pathSegments = args.slice(0, -1); 
    const fieldPath = pathSegments[0]; 
    const filterPath = pathSegments.slice(1);
    
    this._filterConfigs.set(fieldPath, { filterPath, optionKey, pathSegments });
    return this;
  }

  _applyConfiguredFilters(builderToUse, options) {
    if (!this._filterConfigs.size) return;

    builderToUse.query.fields = builderToUse.query.fields.map(field => {
      if (_.isObject(field)) {
        for (const [fieldPath, config] of this._filterConfigs) {
          if (field[fieldPath] && options[config.optionKey] !== undefined) {
            const updatedField = { ...field };
            const filterBasePath = `${fieldPath}.filter`;
            const fullFilterPath = config.filterPath.length > 0 
              ? `${filterBasePath}.${config.filterPath.join('.')}`
              : filterBasePath;
            
            const existingFilter = _.get(field, filterBasePath, {});
            
            _.set(updatedField, fullFilterPath, {
              ..._.get(field, fullFilterPath, {}),
              _eq: options[config.optionKey]
            });
            
            _.set(updatedField, filterBasePath, {
              ...existingFilter,
              ..._.get(updatedField, filterBasePath, {})
            });
            
            return updatedField;
          }
        }
      }
      return field;
    });
  }

  async getAllTableNames() { return this.api.getAllTableNames(); }
  async create(table, payload) { return this.api.create(table, payload); }
  async updateItem(table, id, payload) { return this.api.update(table, id, payload); }
  async deleteItem(table, id) { return this.api.delete(table, id); }
  async withRetry(fn, maxRetries = 3, backoff = 300) { 
    return this.api.withRetry(fn, maxRetries, backoff); 
  }

  polling(intervalMs = 3000) { return this.polling.startPolling(intervalMs); }
  stopPolling(conditionFn) { return this.polling.stopPolling(conditionFn); }

  query(builderInstance) { return new QueryContext(this, builderInstance); }

  watch(key, prop, callback) { return this.store.watch(key, prop, callback); }
  watchDeep(key, path, callback) { return this.store.watchDeep(key, path, callback); }
  listen(key, callback) { return this.store.listen(key, callback); }
  unlisten(key, callback) { return this.store.unlisten(key, callback); }
  update(table, path, value) { return this.store.update(table, path, value); }
  save(key, value) { return this.store.save(key, value); }
  get(key) { return this.store.get(key); }
  getAll(pattern = null) { return this.store.getAll(pattern); }
  getKeys(pattern = null) { return this.store.getKeys(pattern); }
  getValues(pattern = null) { return this.store.getValues(pattern); }
  invalidate(key) { return this.store.invalidate(key); }
  getStore(table) { return this.store.getTableStore(table); }

  clone() {
    const cloned = new DirectusClient(this.baseURL);
    cloned.setToken(this.token);
    cloned.setLanguage(this.language);
    cloned.setHeaderBasedEnabled(this.headerBasedEnabled);
    cloned._filterConfigs = new Map(this._filterConfigs);
    return cloned;
  }

  cleanup() {
    this.polling.cleanup();
    return this;
  }
}

export default DirectusClient;


// WEBPACK FOOTER //
// ./src/app/lib/directus-sdk/index.js